import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import { DrawingOptions, DrawingTypes, LabelAxisViewConfig } from '../drawing.interface'
import { determineChartX, determineChartY } from '../drawing.helper'
import { Coordinate, Time } from 'lightweight-charts'
import { getTimeIndex } from '../../../../util/chart'
import { primaryDrawingColor } from '../../../../assets/colors'

export type ReplayState = {
    time: number;
    price: number;
}

class ReplaySelectionDrawing extends DrawingBase<any, any, ReplayState> {
    backdrop: HTMLElement | null
    scissorIcon: HTMLElement | null;
    withinChart = false;
    fixedDrawing: boolean = true;

    constructor(options: DrawingOptions<any, any, ReplayState>) {
        super(options);
        const container = this.findParent(this.getPaneElement(), undefined, "chart-selector");
        this.backdrop = container?.querySelector(".replay-backdrop-selector") ?? null
        this.scissorIcon = container?.querySelector(".scissor-selector") ?? null
        this.addReplaySelectionMouseListener();
        setTimeout(() => {
            this.primitive?.requestUpdate()
        }, 0)
    }

    resetBackdrop() {
        if (!this.backdrop) return;
        this.backdrop.style.height = '0px';
        this.backdrop.style.width = '0px';
        this.backdrop.style.display = 'none';
    }

    findParent(element: HTMLElement, id?: string, className?: string) {
        if (!element) return;
        let parentNode = element.parentElement;
        while (parentNode) {
            if ((id && parentNode.id === id) || (className && parentNode.classList.contains(className))) return parentNode;
            parentNode = parentNode.parentElement;
        }

        return null;

    }

    draw = (scope: BitmapCoordinatesRenderingScope) => {
        if (!this.backdrop || !this.scissorIcon) return;
        const ctx = scope.context
        let {time, price} = this._state
        
        const data = this._chart.getRenderedPrimaryData();
        
        if (!data.length || time == 0) {
            this.resetBackdrop();
            return
        };

        if (time > data[data.length - 1].time) {
            time = data[data.length - 1].time
        } else if (time < data[0].time) {
            time = data[0].time
        }

        const xCoordinate = this.timeToCoordinate(time as Time)
        const yCoordinate = this.priceToCoordinate(price) || this.canvasHeight/2
        if (!xCoordinate) return;

        this.backdrop.style.height = `calc(100% - ${this._chartApi.timeScale().height()}px)`
        this.backdrop.style.width = `${(this.canvasWidth/window.devicePixelRatio - xCoordinate) + this._chartApi.priceScale('right').width()}px`
        this.backdrop.style.display = 'unset'
        this.scissorIcon.style.top = `${yCoordinate}px`
    }

    addReplaySelectionMouseListener() {
        document.addEventListener('mousemove', this.onReplaySectionMouseMove)
    }

    removeReplaySelectionMouseListener() {
        document.removeEventListener('mousemove', this.onReplaySectionMouseMove)
    }

    onReplaySectionMouseMove = (event: MouseEvent) => {
        if (!this._chartBounds) return;
        const chartX = determineChartX(this._chartApi, this._chartBounds, event.clientX)
        const chartY = determineChartY(this._chartApi, this._chartBounds, event.clientY)
        if ((event.target as HTMLElement).tagName !== "CANVAS" || !chartX || !chartY) {
            if (this.withinChart) {
                this._state.time = 0;
                this.resetBackdrop();
                this._chart.onReplaySelectionMove(0, 0)
            }
            this.withinChart = !!(chartX && chartY);
            return;
        }

        this.withinChart = true;
        const time = this.coordinateToTime(chartX as Coordinate)
        const price = this.coordinateToPrice(chartY as Coordinate) || 0
        if (!time) return;
        this._state.time = time as number;
        this._state.price = price;
        this._chart.onReplaySelectionMove(time as number, price)
    }

    getDrawingType(): DrawingTypes {
        return 'replaySelection'
    }
    destroy(publish?: boolean, pushToHistory?: boolean, chartTypeToHistory?: boolean): void {
        this.removeReplaySelectionMouseListener();
        this.resetBackdrop();
        super.destroy(publish, pushToHistory, chartTypeToHistory);

    }
    updateState(state: Partial<ReplayState>) {
        if (state.time !== this._state.time || state.price !== this._state.price) {
            const data = this._chart.getRenderedPrimaryData();
            const timeIndex = state.time ? getTimeIndex(data, state.time || 0) : null

            const time = timeIndex ? data[timeIndex]?.time : 0;
            this._state = {
                time,
                price: state.price || 0
            }
        }
        this.updateDrawing()
    }
    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { time } = this._state

        return [{
            id: 'time',
            p1: time as Time,
            p2: time as Time,
            fill: true,
            fillColor: '',
            labelColors: {
                p1: primaryDrawingColor,
                p2: primaryDrawingColor
            }
        }]
    }
}

export default ReplaySelectionDrawing